import type { ColumnDef } from '@b1links/core';
import type { EntityMeta, Rec } from '../api/client';
import { findField, formatValue, interpolate, labelFor } from './format';

/**
 * CSV of exactly what the list shows — same columns, labels and formatting as the table on screen, so a figure
 * in the spreadsheet matches the figure the user just looked at.
 */

/** Quote a value for CSV: double quotes are doubled, and anything with a separator/quote/newline is quoted. */
function cell(value: string): string {
  return /[",;\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function toCsv(rows: Rec[], columns: ColumnDef[], meta: EntityMeta | undefined, currencyOf?: (r: Rec) => string | undefined): string {
  const header = columns.map(c => cell(labelFor(c.field, findField(meta, c.field), c.label)));
  const body = rows.map(r => columns.map(c => {
    const text = c.template
      ? interpolate(c.template, r)
      : formatValue(r[c.field], c, findField(meta, c.field), c.format === 'money' ? currencyOf?.(r) : undefined);
    return cell(text ?? '');
  }).join(','));
  return [header.join(','), ...body].join('\r\n');
}

/**
 * Hand the file to the browser. Excel only reads UTF-8 correctly with a byte-order mark, so include one.
 * Returns false where the shell can't save a file (Android WebView blocks blob downloads), so the caller can say so.
 */
export function downloadCsv(filename: string, csv: string): boolean {
  try {
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    return true;
  } catch { return false; }
}

/** "Sales Orders" + today -> "sales-orders-2026-09-21.csv" */
export const csvFileName = (title: string) =>
  `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'export'}-${new Date().toISOString().slice(0, 10)}.csv`;
